import type { Block, Inline } from './parse.ts';

/**
 * An ASSET reference: an image whose source is `asset:<name>` points at a file stored with the
 * document (a guide's screenshots), never at a URL. Names are plain file names.
 */
export const ASSET_SCHEME = 'asset:';
export const ASSET_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/;

/** The asset name of an image source (`asset:login.png` → `login.png`), or null for anything else. */
export function assetName(src: string): string | null {
  if (!src.startsWith(ASSET_SCHEME)) return null;
  const name = src.slice(ASSET_SCHEME.length);
  return ASSET_NAME_RE.test(name) ? name : null;
}

/**
 * A link target that is safe to follow: http(s), mailto, or a relative / fragment reference.
 * Anything else (javascript:, data:, vbscript:, file:) is null and renders as plain text.
 */
export function safeHref(href: string): string | null {
  const h = href.trim();
  if (!h) return null;
  if (/^(https?:|mailto:)/i.test(h)) return h;
  if (/^[#/?.]/.test(h) && !h.startsWith('//')) return h;
  // A bare relative path (no scheme before the first slash).
  if (!/^[A-Za-z][A-Za-z0-9+.-]*:/.test(h) && !h.startsWith('//')) return h;
  return null;
}

function* inlinesOf(blocks: readonly Block[]): Generator<Inline> {
  const walk = function* (nodes: readonly Inline[]): Generator<Inline> {
    for (const n of nodes) {
      yield n;
      if ('children' in n) yield* walk(n.children);
    }
  };
  for (const b of blocks) {
    switch (b.type) {
      case 'heading':
      case 'paragraph':
        yield* walk(b.children);
        break;
      case 'quote':
        yield* inlinesOf(b.children);
        break;
      case 'list':
        for (const item of b.items) yield* inlinesOf(item);
        break;
      case 'table':
        for (const cell of b.header) yield* walk(cell);
        for (const row of b.rows) for (const cell of row) yield* walk(cell);
        break;
      default:
        break;
    }
  }
}

/** Every image of a document, in order. */
export function imagesOf(blocks: readonly Block[]): Extract<Inline, { type: 'image' }>[] {
  const out: Extract<Inline, { type: 'image' }>[] = [];
  for (const n of inlinesOf(blocks)) if (n.type === 'image') out.push(n);
  return out;
}

/** The asset names a document refers to, deduplicated, in order of first use. */
export function assetRefs(blocks: readonly Block[]): string[] {
  const seen = new Set<string>();
  for (const img of imagesOf(blocks)) {
    const name = assetName(img.src);
    if (name) seen.add(name);
  }
  return [...seen];
}

/** A URL-safe slug of a title: lower case, words joined by `-`, at most `max` characters. */
export function slugify(text: string, max = 80): string {
  const s = text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/, '');
  return s;
}
